/** Fraction of the track's travel distance the handle must cross to count as "unlocked". */
export const SWIPE_UNLOCK_THRESHOLD_RATIO = 0.86;

/** Hold-to-confirm countdown length, in whole seconds (displayed 3 → 2 → 1 → 0). */
export const HOLD_TO_UNLOCK_SECONDS = 3;

/** Decreasing step sizes (% of total travel) for the swipe haptic milestone ladder. */
export const SWIPE_HAPTIC_DECREASING_STEP_PERCENTS = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1] as const;

/** Step size (% of total travel) once the decreasing ladder reaches 1%. */
export const SWIPE_HAPTIC_ONE_PERCENT_STEP = 0.01;

/** Final dense-zone spacing between milestones (px). */
export const SWIPE_HAPTIC_DENSE_STEP_PX = 1;

/** Max x-offset (px) the draggable handle can travel inside the track. */
export function swipeUnlockTrackTravel(trackWidthPx: number, handleWidthPx: number): number {
  return Math.max(0, trackWidthPx - handleWidthPx);
}

/** True once the handle has been dragged far enough right to count as "reached unlock". */
export function isSwipeUnlockThresholdReached(
  offsetXPx: number,
  trackWidthPx: number,
  handleWidthPx: number,
): boolean {
  const travel = swipeUnlockTrackTravel(trackWidthPx, handleWidthPx);
  if (travel <= 0) return false;
  return offsetXPx / travel >= SWIPE_UNLOCK_THRESHOLD_RATIO;
}

/** Seconds remaining on the hold-to-unlock countdown after holding for `elapsedMs`. */
export function holdCountdownRemaining(elapsedMs: number): number {
  return Math.max(0, HOLD_TO_UNLOCK_SECONDS - Math.floor(elapsedMs / 1000));
}

/** Light-impact pulse count for each visible countdown second (3 → 3, 2 → 2, 1 → 1). */
export function holdCountdownHapticPulseCount(remaining: number): number {
  if (remaining < 1 || remaining > HOLD_TO_UNLOCK_SECONDS) return 0;
  return remaining;
}

/** True when `currentRemaining` crosses a new countdown second worth haptic feedback. */
export function shouldFireHoldCountdownHaptic(
  previousRemaining: number | null,
  currentRemaining: number,
): boolean {
  if (currentRemaining < 1 || currentRemaining > HOLD_TO_UNLOCK_SECONDS) return false;
  return previousRemaining !== currentRemaining;
}

/** Drag progress as 0..1 along the track (0 = start, 1 = full travel). */
export function dragProgressRatio(
  offsetXPx: number,
  trackWidthPx: number,
  handleWidthPx: number,
): number {
  const travel = swipeUnlockTrackTravel(trackWidthPx, handleWidthPx);
  if (travel <= 0) return 0;
  return Math.min(1, Math.max(0, offsetXPx / travel));
}

/** True once when drag progress crosses the unlock threshold (for a one-shot confirm pulse). */
export function didCrossSwipeUnlockThreshold(prevProgress: number, nextProgress: number): boolean {
  return (
    prevProgress < SWIPE_UNLOCK_THRESHOLD_RATIO && nextProgress >= SWIPE_UNLOCK_THRESHOLD_RATIO
  );
}

/**
 * Build sorted milestone positions (px) from 0 up to full drag travel.
 *
 * Ladder: 10% → 9% → … → 1% of total travel, then 1% steps, then ~1px dense spacing
 * through the end of the track. Independent of the unlock overlay threshold.
 */
export function buildSwipeHapticMilestones(travelPx: number): number[] {
  if (travelPx <= 0) return [];

  const milestones: number[] = [];
  let positionPx = 0;

  for (const stepPercent of SWIPE_HAPTIC_DECREASING_STEP_PERCENTS) {
    positionPx += (stepPercent / 100) * travelPx;
    if (positionPx >= travelPx) return milestones;
    milestones.push(positionPx);
  }

  const onePercentPx = travelPx * SWIPE_HAPTIC_ONE_PERCENT_STEP;

  while (true) {
    const nextPositionPx = positionPx + onePercentPx;
    const remainingAfterStepPx = travelPx - nextPositionPx;
    if (remainingAfterStepPx < onePercentPx - 1e-9) break;
    positionPx = nextPositionPx;
    milestones.push(positionPx);
  }

  const denseStepPx = Math.max(1, SWIPE_HAPTIC_DENSE_STEP_PX);
  while (positionPx + denseStepPx <= travelPx) {
    positionPx += denseStepPx;
    milestones.push(positionPx);
  }

  return milestones;
}

/** Milestone positions expressed as % of total travel (0–100 scale). */
export function swipeHapticMilestonePercents(travelPx: number): number[] {
  return buildSwipeHapticMilestones(travelPx).map(
    (milestonePx) => (milestonePx / travelPx) * 100,
  );
}

/** Milestones crossed when the handle moves from `prevPx` to `nextPx` (either direction). */
export function getCrossedSwipeHapticMilestones(
  prevPx: number,
  nextPx: number,
  milestones: number[],
): number[] {
  if (prevPx === nextPx || milestones.length === 0) return [];

  const goingForward = nextPx > prevPx;
  const crossed: number[] = [];

  for (const milestone of milestones) {
    if (goingForward) {
      if (prevPx < milestone && nextPx >= milestone) {
        crossed.push(milestone);
      }
    } else if (prevPx > milestone && nextPx <= milestone) {
      crossed.push(milestone);
    }
  }

  if (!goingForward) {
    crossed.reverse();
  }

  return crossed;
}
